/**
 * Model Settings Manager Component
 * Per-model configuration with save defaults
 * Validates: Requirements 21.1, 21.2, 21.3, 21.4
 */
import { useState, useEffect } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { ModelSettings, LLMProvider } from '../../types';
import { AVAILABLE_MODELS, ModelOption } from '../chat/ModelSelector';

// Default settings for each provider
const DEFAULT_SETTINGS: Record<LLMProvider, ModelSettings> = {
  openai: { temperature: 0.7, maxTokens: 4096, topP: 1 },
  anthropic: { temperature: 0.7, maxTokens: 4096, topP: 1 },
  google: { temperature: 0.7, maxTokens: 8192, topP: 1 },
  grok: { temperature: 0.7, maxTokens: 4096, topP: 1 },
  meta: { temperature: 0.7, maxTokens: 4096, topP: 1 },
  deepseek: { temperature: 0.7, maxTokens: 4096, topP: 1 },
  qwen: { temperature: 0.7, maxTokens: 4096, topP: 1 },
};

// Provider display info
const PROVIDER_INFO: Record<LLMProvider, { name: string; color: string }> = {
  openai: { name: 'OpenAI', color: 'bg-green-600' },
  anthropic: { name: 'Anthropic', color: 'bg-orange-600' },
  google: { name: 'Google', color: 'bg-blue-600' },
  grok: { name: 'Grok', color: 'bg-purple-600' },
  meta: { name: 'Meta', color: 'bg-indigo-600' },
  deepseek: { name: 'DeepSeek', color: 'bg-cyan-600' },
  qwen: { name: 'Qwen', color: 'bg-pink-600' },
};

interface ModelSettingsEditorProps {
  model: ModelOption;
  settings: ModelSettings;
  isDefault: boolean;
  onSave: (settings: ModelSettings) => Promise<void>;
  onSetDefault: () => Promise<void>;
  onReset: () => Promise<void>;
  isLoading: boolean;
}

function ModelSettingsEditor({
  model,
  settings,
  isDefault,
  onSave,
  onSetDefault,
  onReset,
  isLoading,
}: ModelSettingsEditorProps) {
  const [localSettings, setLocalSettings] = useState<ModelSettings>(settings);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
    setHasChanges(false);
  }, [settings]);

  const handleChange = (key: keyof ModelSettings, value: number) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(localSettings);
    setHasChanges(false);
  };

  const providerInfo = PROVIDER_INFO[model.provider];
  const defaults = DEFAULT_SETTINGS[model.provider];

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${providerInfo.color}`} />
          <div>
            <h4 className="text-white font-medium">{model.name}</h4>
            <p className="text-xs text-gray-500">{providerInfo.name}</p>
          </div>
        </div>
        {isDefault && (
          <span className="px-2 py-1 text-xs bg-primary-600/20 text-primary-400 rounded">
            Default
          </span>
        )}
      </div>

      <div className="space-y-4">
        {/* Temperature */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-300">Temperature</label>
            <span className="text-sm text-gray-400 font-mono">
              {localSettings.temperature?.toFixed(2) ?? defaults.temperature?.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="2"
            step="0.01"
            value={localSettings.temperature ?? defaults.temperature}
            onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Controls randomness. Lower = more focused, higher = more creative.
          </p>
        </div>

        {/* Max Tokens */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-300">Max Tokens</label>
            <span className="text-sm text-gray-400 font-mono">
              {localSettings.maxTokens ?? defaults.maxTokens}
            </span>
          </div>
          <input
            type="range"
            min="256"
            max="32768"
            step="256"
            value={localSettings.maxTokens ?? defaults.maxTokens}
            onChange={(e) => handleChange('maxTokens', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum number of tokens in the response.
          </p>
        </div>

        {/* Top P */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-300">Top P</label>
            <span className="text-sm text-gray-400 font-mono">
              {localSettings.topP?.toFixed(2) ?? defaults.topP?.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={localSettings.topP ?? defaults.topP}
            onChange={(e) => handleChange('topP', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Nucleus sampling. Lower = more focused on likely tokens.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={onReset}
          disabled={isLoading}
          className="text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50"
        >
          Reset to defaults
        </button>
        <div className="flex gap-2">
          {!isDefault && (
            <button
              onClick={onSetDefault}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              Set as Default
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ModelSettingsManager() {
  const {
    modelSettings,
    defaultModel,
    settingsLoading,
    fetchModelSettings,
    updateModelSettings,
    setDefaultModel,
    resetModelSettings,
  } = useSettingsStore();

  const [selectedProvider, setSelectedProvider] = useState<LLMProvider | 'all'>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    fetchModelSettings();
  }, [fetchModelSettings]);

  // Filter models by provider
  const filteredModels = selectedProvider === 'all'
    ? AVAILABLE_MODELS
    : AVAILABLE_MODELS.filter((m) => m.provider === selectedProvider);

  // Group models by provider for display
  const modelsByProvider = filteredModels.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<LLMProvider, ModelOption[]>);

  const handleSaveSettings = async (modelId: string, settings: ModelSettings) => {
    setIsSubmitting(true);
    await updateModelSettings(modelId, settings);
    setIsSubmitting(false);
  };

  const handleSetDefault = async (modelId: string) => {
    setIsSubmitting(true);
    await setDefaultModel(modelId);
    setIsSubmitting(false);
  };

  const handleReset = async (modelId: string) => {
    setIsSubmitting(true);
    await resetModelSettings(modelId);
    setIsSubmitting(false);
  };

  const getSettingsForModel = (model: ModelOption): ModelSettings => {
    return modelSettings[model.id] || DEFAULT_SETTINGS[model.provider];
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Model Settings</h2>
        
        {/* Provider Filter */}
        <select
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value as LLMProvider | 'all')}
          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Providers</option>
          {Object.entries(PROVIDER_INFO).map(([id, info]) => (
            <option key={id} value={id}>
              {info.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <p className="text-gray-400 mb-6">
          Configure default settings for each model including temperature, max tokens, and other parameters.
          These settings will be applied to new conversations.
        </p>

        {settingsLoading && Object.keys(modelSettings).length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-5 w-5 text-gray-400" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="ml-2 text-gray-400">Loading settings...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(modelsByProvider).map(([provider, models]) => {
              const providerInfo = PROVIDER_INFO[provider as LLMProvider];
              return (
                <div key={provider}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-2 h-2 rounded-full ${providerInfo.color}`} />
                    <h3 className="text-sm font-medium text-gray-300">{providerInfo.name}</h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {models.map((model) => (
                      <ModelSettingsEditor
                        key={model.id}
                        model={model}
                        settings={getSettingsForModel(model)}
                        isDefault={defaultModel === model.id}
                        onSave={(settings) => handleSaveSettings(model.id, settings)}
                        onSetDefault={() => handleSetDefault(model.id)}
                        onReset={() => handleReset(model.id)}
                        isLoading={isSubmitting}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
